/**
 * 全局搜索的类型登记表（方案 2026-10-22-global-search-design §2.1、§7）：类型 → 图标、组顺序、怎么打开、第二行说明。
 * 只有登记在这里的类型会请求、会显示——后端多登记一类，不自动出现在搜索框里。
 * 打开方式复用 entity-flyout（useOpenEntity）：task / reservation / intent 在当前页开 flyout，其余跳页面（entityLinks.ENTITY_PAGES）。
 */
import type { LucideIcon } from 'lucide-react'
import { Boxes, CalendarDays, CircleAlert, FileText, Home, Package, User, UserRound, Wrench } from 'lucide-react'
import type { TFunction } from 'i18next'
import { formatDate } from '@/utils/dateFormat'
import { entityPagePath } from '@/components/entity-flyout/entityLinks'
import { listingKindLabel } from '@/config/listing-kind-label'

export const ENTITY_SEARCH_TYPES = [
  'task', 'reservation', 'intent', 'listing', 'group', 'user', 'owner', 'asset',
] as const
export type EntitySearchType = (typeof ENTITY_SEARCH_TYPES)[number]

/** 后端把虚拟地点放在 `place` 这一类。界面上它们和出租房、办公仓库同组，打开也是同一个详情页 */
export const ENTITY_SEARCH_REQUEST_TYPES = [...ENTITY_SEARCH_TYPES, 'place'] as const

/** 结果组：「页面」（侧栏菜单，本地过滤）+ 各实体类型。同级按这个顺序 */
export type SearchGroupKey = 'page' | EntitySearchType
export const GROUP_ORDER: readonly SearchGroupKey[] = ['page', ...ENTITY_SEARCH_TYPES]

export function isEntitySearchType(type: string): type is EntitySearchType {
  return (ENTITY_SEARCH_TYPES as readonly string[]).includes(type)
}

interface DetailContext {
  t: TFunction
  timezone: string
}

interface SearchTypeDef {
  icon: LucideIcon
  /** flyout 类的整页地址（⌘Enter 新标签页打开用）。跳页面的类型不给：用 entityLinks.ENTITY_PAGES */
  fullPage?: (id: string) => string
  /** 这一类有更多时「在列表里看」：列表页读 `?q=`（useListPageUrlParams）。没有就不给链接 */
  moreHref?: (q: string) => string
  /** 第二行说明（分得清同名对象）。没有就不显示 */
  detail?: (d: Record<string, unknown>, ctx: DetailContext) => string | null
}

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : null)
const join = (...parts: Array<string | null | undefined>): string | null => {
  const kept = parts.filter((p): p is string => !!p)
  return kept.length ? kept.join(' · ') : null
}
const id = encodeURIComponent

export const SEARCH_TYPES: Readonly<Record<EntitySearchType, SearchTypeDef>> = {
  task: { icon: Wrench, fullPage: (v) => `/tasks/${id(v)}` },
  reservation: {
    icon: CalendarDays,
    fullPage: (v) => `/reservations/${id(v)}`,
    moreHref: (q) => `/reservations?q=${id(q)}`,
    detail: (d, { t, timezone }) => {
      const checkIn = text(d.check_in)
      const checkOut = text(d.check_out)
      const dates = checkIn && checkOut ? `${formatDate(checkIn, timezone)} – ${formatDate(checkOut, timezone)}` : null
      const status = text(d.status)
      return join(text(d.property), dates, status && t(`reservations:status.${status}`, { defaultValue: status }),
        text(d.confirmation_code))
    },
  },
  intent: { icon: CircleAlert, fullPage: (v) => `/intents/${id(v)}` },
  listing: {
    icon: Home,
    // 办公仓库、虚拟地点也在这一组，写出种类；出租房不写。虚拟地点没有地址，写使用人
    detail: (d) => {
      const kind = text(d.listingKind)
      const users = Array.isArray(d.users) ? d.users.map(text).filter(Boolean).join(', ') : null
      return join(kind && kind !== 'RENTAL' ? listingKindLabel(kind) : null, text(d.group), text(d.address), users)
    },
  },
  group: {
    icon: Boxes,
    detail: (d) => text(d.code),
  },
  user: {
    icon: User,
    detail: (d) => join(text(d.department), text(d.position), text(d.username)),
  },
  owner: { icon: UserRound },
  asset: {
    icon: Package,
    moreHref: (q) => `/asset-mgr/assets?q=${id(q)}`,
    detail: (d, { t }) => {
      const status = text(d.status)
      return join(text(d.product), text(d.location), status && t(`assets:statusLabels.${status}`, { defaultValue: status }))
    },
  },
}

/** 整页地址：flyout 类给详情页，其余同 entityLinks（点击打开也走它） */
export function fullPagePath(type: EntitySearchType, entityId: string): string | null {
  return SEARCH_TYPES[type].fullPage?.(entityId) ?? entityPagePath(type, entityId)
}

/** 「页面」组的图标 */
export const PAGE_ICON: LucideIcon = FileText
